/**
 * The two decisions the auth slice makes about an MSAL account, as pure functions.
 *
 * Extracted from `AuthProvider` so they can be covered without mounting React or faking
 * MSAL: the tenant gate is the piece of this feature with real security weight, and it was
 * previously reachable only through a hook. The provider keeps the MSAL plumbing; the
 * rules live here.
 *
 * Neither function reads configuration. `expectedTenantId` is passed in so a test can
 * state the expectation it is exercising, rather than depending on whichever `.env.<mode>`
 * the run happened to load.
 */
import type { AccountInfo } from '@azure/msal-browser';
import type { AuthAccount, UserProfile } from './store/authSlice';

/** An ID-token claim, or `''` when absent or not a string. */
function claimString(account: AccountInfo, key: string): string {
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const value = claims?.[key];
  return typeof value === 'string' ? value : '';
}

/**
 * Whether an account is from the tenant this flavor admits — TESTTRS in NPE, the consumer
 * tenant in PPE / PROD.
 *
 * A backstop, not the control: the authority is tenanted, so Entra refuses a foreign
 * account at its own sign-in page and this only catches a misconfigured authority or an
 * account that reached the cache some other way.
 *
 * Fail-closed on both sides. An empty `expectedTenantId` — an unset `VITE_MSAL_TENANT_ID`
 * — admits nobody rather than everybody, and an account carrying no tenant at all is not
 * admitted by an empty expectation either. The comparison is case- and
 * whitespace-insensitive because a GUID in an `.env` file and a `tid` claim are both free
 * to differ in casing.
 */
export function isTenantAdmitted(account: AccountInfo, expectedTenantId: string): boolean {
  const expected = expectedTenantId.trim().toLowerCase();
  if (!expected) {
    return false;
  }

  // `tenantId` is MSAL's parse of the account; the `tid` claim is the same value as the
  // token carries it, and covers an account MSAL filled in only partially.
  const tenantId = (account.tenantId || claimString(account, 'tid')).trim().toLowerCase();
  return tenantId !== '' && tenantId === expected;
}

/**
 * The profile the shell renders, read from ID-token claims rather than Microsoft Graph —
 * sign-in needs no `User.Read` consent (see config/authConfig.ts).
 *
 * Every field that is rendered on its own falls back to `username`, which MSAL always
 * populates, so a sparse token still yields a usable header. The name parts are left empty
 * instead, rather than putting an address where a given name belongs.
 */
export function toUserProfile(account: AccountInfo): UserProfile {
  const name = account.name ?? '';
  return {
    displayName: name || account.username,
    givenName: claimString(account, 'given_name') || name.split(' ')[0] || '',
    surname: claimString(account, 'family_name') || name.split(' ').slice(1).join(' ') || '',
    mail:
      claimString(account, 'email') || claimString(account, 'preferred_username') || account.username,
    userPrincipalName:
      claimString(account, 'upn') || claimString(account, 'preferred_username') || account.username,
  };
}

/**
 * Narrows an MSAL account to the four fields the store mirrors.
 *
 * The point is what it drops: `AccountInfo` carries `idTokenClaims` and can carry the raw
 * `idToken`, and mirroring it whole put the ID token into Redux and the DevTools timeline.
 * Listing the fields explicitly — rather than deleting keys from a copy — means a future
 * MSAL version adding another sensitive field cannot widen what gets stored.
 */
export function toAuthAccount(account: AccountInfo): AuthAccount {
  return {
    homeAccountId: account.homeAccountId,
    username: account.username,
    tenantId: account.tenantId,
    name: account.name ?? '',
  };
}
